import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useFormState} from 'react-hook-form';
import {FormStateEnum, ModuleEnum} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {EditIcon} from 'src/design/atoms/icons/CommonIcons';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {SaveButton} from 'src/design/molecules/SaveButton';
import {SideDrawer} from 'src/design/organisms/SideDrawer';
import {FormTransitionBar} from 'src/app/forms/FormTransitionBar';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import type {ProactFormApi} from 'src/app/forms/engine/useProactForm';
import {FormViewComponentV2} from 'src/app/forms/FormViewComponent/FormViewComponentV2';
import {getFormPillConfig} from 'src/app/forms/formHelpers';
import type {UseFormSubmissionEditorOptions} from 'src/app/forms/hooks/useFormSubmissionEditor';
import {useFormSubmissionEditor} from 'src/app/forms/hooks/useFormSubmissionEditor';
import {useFormSubmissionViewer} from 'src/app/forms/hooks/useFormSubmissionViewer';
import {OfflineCaptureProvider} from 'src/app/forms/offline/OfflineCaptureContext';
import {PathUtility} from 'src/shared/constants/PathBuilder';

export type FormSubmissionDrawerMode = 'create' | 'view' | 'edit';

/** Where a new submission attaches: a task assignment or an extension tab target. */
export type FormSubmissionCreateOptions = Omit<
  UseFormSubmissionEditorOptions,
  'formDefinitionId' | 'formSubmissionId'
>;

export interface FormSubmissionDrawerProps {
  opened: boolean;
  onClose: () => void;
  onSubmissionChange?: () => void;
  formDefinitionId: number;
  createOptions: FormSubmissionCreateOptions;
  /** Set to open in view/edit mode for an existing submission. */
  formSubmissionId?: number;
  initialMode?: FormSubmissionDrawerMode;
  'data-testid'?: string;
}

/**
 * Side drawer that creates, views and edits one form submission without leaving the page.
 * @registry Form submission create/view/edit drawer with transitions and an unsaved-changes guard.
 */
export const FormSubmissionDrawer: React.FC<FormSubmissionDrawerProps> = ({
  opened,
  onClose,
  onSubmissionChange,
  formDefinitionId,
  createOptions,
  formSubmissionId: initialSubmissionId,
  initialMode = 'create',
  'data-testid': testId = 'form-submission-drawer',
}) => {
  const [mode, setMode] = useState<FormSubmissionDrawerMode>(initialMode);
  const [activeSubmissionId, setActiveSubmissionId] = useState<
    number | undefined
  >(initialSubmissionId);
  const isDirtyRef = useRef(false);
  const [confirmClose, setConfirmClose] = useState(false);

  useEffect(() => {
    if (opened) {
      setMode(initialMode);
      setActiveSubmissionId(initialSubmissionId);
      isDirtyRef.current = false;
    }
  }, [opened, initialMode, initialSubmissionId]);

  const handleClose = useCallback(() => {
    if ((mode === 'create' || mode === 'edit') && isDirtyRef.current) {
      setConfirmClose(true);
    } else {
      onClose();
    }
  }, [mode, onClose]);

  const handleConfirmDiscard = () => {
    setConfirmClose(false);
    isDirtyRef.current = false;
    onClose();
  };

  const markDirty = useCallback(() => {
    isDirtyRef.current = true;
  }, []);

  // A redirect transition has already persisted the submission, so close without the guard.
  const handleRedirect = useCallback(() => {
    isDirtyRef.current = false;
    onSubmissionChange?.();
    onClose();
  }, [onClose, onSubmissionChange]);

  return (
    <>
      <SideDrawer data-testid={testId} opened={opened} onClose={handleClose}>
        {mode === 'view' && activeSubmissionId ? (
          <ViewContent
            formDefinitionId={formDefinitionId}
            formSubmissionId={activeSubmissionId}
            onEdit={() => setMode('edit')}
            onSubmissionChange={onSubmissionChange}
            onRedirect={handleRedirect}
          />
        ) : mode === 'edit' && activeSubmissionId ? (
          <EditContent
            formDefinitionId={formDefinitionId}
            formSubmissionId={activeSubmissionId}
            onDirty={markDirty}
            onRedirect={handleRedirect}
            onSaved={() => {
              isDirtyRef.current = false;
              onSubmissionChange?.();
              setMode('view');
            }}
          />
        ) : (
          <CreateContent
            formDefinitionId={formDefinitionId}
            createOptions={createOptions}
            onDirty={markDirty}
            onRedirect={handleRedirect}
            onCreated={(newId: number) => {
              isDirtyRef.current = false;
              onSubmissionChange?.();
              setActiveSubmissionId(newId);
              setMode('view');
            }}
            onOfflineQueued={handleRedirect}
          />
        )}
      </SideDrawer>
      <ConfirmDialog
        opened={confirmClose}
        onClose={() => setConfirmClose(false)}
        onConfirm={handleConfirmDiscard}
        title="Unsaved Changes"
        body="You have unsaved changes. Are you sure you want to close?"
        confirmationButtonColor="red"
        buttonTextYes="Discard Changes"
        buttonTextNo="Cancel"
        size="sm"
      />
    </>
  );
};

interface CreateContentProps {
  formDefinitionId: number;
  createOptions: FormSubmissionCreateOptions;
  onDirty: () => void;
  onRedirect: () => void;
  onCreated: (newId: number) => void;
  /** Queued to the offline outbox: there is no server id to view yet. */
  onOfflineQueued: () => void;
}

const CreateContent: React.FC<CreateContentProps> = ({
  formDefinitionId,
  createOptions,
  onDirty,
  onRedirect,
  onCreated,
  onOfflineQueued,
}) => {
  const {
    formDefinition,
    formApi,
    isLoading,
    isSavePending,
    currentFormId,
    stateMachine,
    handleValidateAndSave,
  } = useFormSubmissionEditor({...createOptions, formDefinitionId});

  const handleSubmitted = () => {
    if (currentFormId.current) {
      onCreated(currentFormId.current);
    }
  };

  return (
    <>
      <SideDrawer.Header title={formDefinition?.title ?? 'New Submission'} />
      <SideDrawer.Body loading={isLoading}>
        {!isLoading && (
          <OfflineCaptureProvider enabled>
            <ProactForm form={formApi}>
              <DirtyNotifier formApi={formApi} onDirty={onDirty} />
              <ProactFormGrid />
            </ProactForm>
          </OfflineCaptureProvider>
        )}
      </SideDrawer.Body>
      {!isLoading && stateMachine && (
        <SideDrawer.Footer>
          <FormTransitionBar
            preTransitionButtonNodes={[
              <SaveButton
                key="save"
                loading={isSavePending}
                onClick={async () => {
                  const resp = await handleValidateAndSave();
                  if (resp.offlineQueued) {
                    onOfflineQueued();
                  } else if (resp.continue) {
                    handleSubmitted();
                  }
                }}
                mr="md"
              />,
            ]}
            onTransitioning={handleValidateAndSave}
            onOfflineQueued={onOfflineQueued}
            onRedirect={onRedirect}
            module={ModuleEnum.Forms}
            stateMachine={stateMachine}
            stateId={FormStateEnum.Draft}
            transitionBaseUrl={`form/${currentFormId.current}/transition`}
            onRefreshTransition={handleSubmitted}
            bypassPermissions
            invalidateTagsOnComplete={['Forms', 'TaskAssignments']}
          />
        </SideDrawer.Footer>
      )}
    </>
  );
};

interface ViewContentProps {
  formDefinitionId: number;
  formSubmissionId: number;
  onEdit: () => void;
  onSubmissionChange?: () => void;
  onRedirect: () => void;
}

const ViewContent: React.FC<ViewContentProps> = ({
  formDefinitionId,
  formSubmissionId,
  onEdit,
  onSubmissionChange,
  onRedirect,
}) => {
  const {
    form,
    formData,
    formMeta,
    isLoading,
    stateMachine,
    isMachineLoading,
    canEdit,
    refetch,
  } = useFormSubmissionViewer({
    formDefinitionId,
    formSubmissionId,
  });

  const pillOptions = getFormPillConfig(
    form?.state ?? FormStateEnum.Draft,
    form?.internalIdentifier
  );

  const handleRefreshTransition = () => {
    void refetch();
    onSubmissionChange?.();
  };

  return (
    <>
      <SideDrawer.Header
        title={form?.formDefinition?.name ?? 'Form Submission'}
        badges={pillOptions}
        fullPageHref={PathUtility.FormSubmission(
          formDefinitionId,
          formSubmissionId,
          'content'
        )}
        actions={
          canEdit && (
            <Button
              size="xs"
              variant="subtle"
              color="black"
              leftSection={<EditIcon />}
              onClick={onEdit}>
              Edit
            </Button>
          )
        }
      />
      <SideDrawer.Body loading={isLoading || isMachineLoading}>
        {!isLoading && (
          <FormViewComponentV2 formData={formData} formMeta={formMeta} />
        )}
      </SideDrawer.Body>
      {!isLoading && stateMachine && form && !form.parentId && (
        <SideDrawer.Footer>
          <FormTransitionBar
            stateMachine={stateMachine}
            stateId={form?.state ?? FormStateEnum.Invalid}
            transitionBaseUrl={`form/${formSubmissionId}/transition`}
            onRefreshTransition={handleRefreshTransition}
            onRedirect={onRedirect}
            bypassPermissions
            invalidateTagsOnComplete={['Forms', 'TaskAssignments']}
          />
        </SideDrawer.Footer>
      )}
    </>
  );
};

interface EditContentProps {
  formDefinitionId: number;
  formSubmissionId: number;
  onDirty: () => void;
  onRedirect: () => void;
  onSaved: () => void;
}

const EditContent: React.FC<EditContentProps> = ({
  formDefinitionId,
  formSubmissionId,
  onDirty,
  onRedirect,
  onSaved,
}) => {
  const {
    form,
    formDefinition,
    formApi,
    isLoading,
    isSavePending,
    currentFormId,
    stateMachine,
    handleValidateAndSave,
  } = useFormSubmissionEditor({
    formDefinitionId,
    formSubmissionId,
  });

  const pillOptions = getFormPillConfig(
    form?.state ?? FormStateEnum.Draft,
    form?.internalIdentifier
  );

  return (
    <>
      <SideDrawer.Header
        title={formDefinition?.title ?? 'Edit Submission'}
        badges={pillOptions}
      />
      <SideDrawer.Body loading={isLoading}>
        {!isLoading && (
          <ProactForm form={formApi}>
            <DirtyNotifier formApi={formApi} onDirty={onDirty} />
            <ProactFormGrid />
          </ProactForm>
        )}
      </SideDrawer.Body>
      {!isLoading && stateMachine && (
        <SideDrawer.Footer>
          <FormTransitionBar
            preTransitionButtonNodes={[
              <SaveButton
                key="save"
                loading={isSavePending}
                onClick={async () => {
                  const resp = await handleValidateAndSave();
                  if (resp.continue || resp.offlineQueued) {
                    onSaved();
                  }
                }}
                mr="md"
              />,
            ]}
            onTransitioning={handleValidateAndSave}
            onRedirect={onRedirect}
            module={ModuleEnum.Forms}
            stateMachine={stateMachine}
            stateId={form?.state ?? FormStateEnum.Draft}
            transitionBaseUrl={`form/${currentFormId.current}/transition`}
            onRefreshTransition={onSaved}
            bypassPermissions
            invalidateTagsOnComplete={['Forms', 'TaskAssignments']}
          />
        </SideDrawer.Footer>
      )}
    </>
  );
};

interface DirtyNotifierProps {
  formApi: ProactFormApi;
  onDirty: () => void;
}

/** Notifies the drawer when the form becomes dirty so it can guard close. */
const DirtyNotifier: React.FC<DirtyNotifierProps> = ({formApi, onDirty}) => {
  const {isDirty} = useFormState({control: formApi.methods.control});

  useEffect(() => {
    if (isDirty) {
      onDirty();
    }
  }, [isDirty, onDirty]);

  return null;
};
